import { useDataStore } from '@/pinia'
import { resetStore } from '@/pinia/storeRegistry'
import type { RefreshOutcome } from '@/types/Utils/ITokenManager'
import { GetRefreshToken, GetToken, HasSessionHint } from '@/utils-ts'
import { TokenManager } from '@/utils-ts/TokenManager'
import { createRouter, createWebHistory } from 'vue-router'

let routeRefreshPromise: Promise<RefreshOutcome> | null = null

async function refreshAccessTokenForRoute(): Promise<RefreshOutcome | null> {
  const tokenManager = TokenManager.GetInstance()
  if (tokenManager.GetToken() && !tokenManager.IsTokenExpired()) {
    return null
  }

  const refreshPromise =
    routeRefreshPromise ?? (routeRefreshPromise = tokenManager.RefreshAccessToken())

  try {
    return await refreshPromise
  } finally {
    if (routeRefreshPromise === refreshPromise) {
      routeRefreshPromise = null
    }
  }
}

enum PageIndex {
  HOME = 0,
  ANALYTICS = 1,
  COMPANY_SETTINGS = 2,
  EMPLOYEE_MANAGEMENT = 3,
  SHARING_SETTINGS = 4,
  SHARING_CONFIRMATIONS = 5,
  ANNOUNCEMENT_SETTINGS = 6,
  PERFORMANCE_MANAGEMENT = 7,
  CUSTOM_REWARD = 8,
  SURVEY = 9,
  ANSWERWIN = 10,
  CHALLENGE_SETTINGS = 11,
  WORD_GAME = 12,
  NOTIFICATION_SETTINGS = 13,
  COMMUNITY = 14,
  GDPR = 15,
  SECURITY = 16,
  SYSTEM = 17,
  MESSAGE_TEMPLATES = 18,
  LANGUAGE_SETTINGS = 19,
}

const routes = [
  // #region Empty Layout Pages
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/User/Login.vue'),
    meta: {
      isDefault: false,
      pageResource: null,
      reference: null,
      store: null,
    },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/User/ForgotPassword.vue'),
    meta: {
      isDefault: false,
      pageResource: null,
      reference: null,
      store: null,
    },
  },
  {
    path: '/reset-password/:token/:companyCode/:isSms',
    name: 'reset-password',
    component: () => import('../views/User/ResetPassword.vue'),
    alias: [
      '/sifremi-sifirla/:token/:companyCode/:isSms',
      '/en/reset-password/:token/:companyCode/:isSms',
      '/de/reset-password/:token/:companyCode/:isSms',
      '/ru/reset-password/:token/:companyCode/:isSms',
      '/pt/reset-password/:token/:companyCode/:isSms',
      '/th/reset-password/:token/:companyCode/:isSms',
      '/id/reset-password/:token/:companyCode/:isSms',
    ],
    meta: {
      isDefault: false,
      pageResource: null,
      reference: null,
      store: null,
    },
  },
  {
    path: '/multi-factor-authentication',
    name: 'multi-factor-authentication',
    component: () => import('../views/User/MultiFactorAuth.vue'),
    meta: {
      isDefault: false,
      pageResource: null,
      reference: null,
      store: null,
    },
  },
  {
    path: '/company-code-auth',
    name: 'company-code-auth',
    component: () => import('../views/User/CodeAuth.vue'),
    meta: {
      isDefault: false,
      pageResource: null,
      reference: null,
      store: null,
    },
  },
  // #endregion

  // #region 0-Home
  {
    path: '/',
    name: 'home',
    component: () => import('../views/Home/Homepage.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_homepage',
      reference: null,
      store: null,
      index: PageIndex.HOME,
    },
  },
  // #endregion

  // #region 1-Analytics & Report
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/Analytics/Dashboard.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_dashboard',
      reference: 'admin_pages_analytics_dashboard',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/spendable-point-report',
    name: 'spendable-point-report',
    component: () => import('../views/Analytics/SpendablePointReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_pointreport',
      reference: 'admin_pages_analytics_spendablepointreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/distributable-point-report',
    name: 'distributable-point-report',
    component: () => import('../views/Analytics/DistributablePointReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_distributablepointreport',
      reference: 'admin_pages_analytics_distributablepointreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/point-reset-report',
    name: 'point-reset-report',
    component: () => import('../views/Analytics/PointResetReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_pointresetreport',
      reference: 'admin_pages_analytics_pointresetreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/reward-report',
    name: 'reward-report',
    component: () => import('../views/Analytics/RewardReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_rewardreport',
      reference: 'admin_pages_analytics_rewardreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/post-report',
    name: 'post-report',
    component: () => import('../views/Analytics/PostReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_report_index_givings',
      reference: 'admin_pages_analytics_postreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/given-reward-report',
    name: 'given-reward-report',
    component: () => import('../views/Analytics/GivenRewardReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_givenrewardreport',
      reference: 'admin_pages_analytics_givenrewardreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/portal-login-report',
    name: 'portal-login-report',
    component: () => import('../views/Analytics/PortalLoginReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_loginreport',
      reference: 'admin_pages_analytics_portalloginreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/message-report',
    name: 'message-report',
    component: () => import('../views/Analytics/MessageReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_messagereport',
      reference: 'admin_pages_analytics_messagereport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/post-based-detailed-report',
    name: 'post-based-detailed-report',
    component: () => import('../views/Analytics/PostBasedDetailedReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_analytics_postbaseddetailedreport',
      reference: 'admin_pages_analytics_postbaseddetailedreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/community-report',
    name: 'community-report',
    component: () => import('../views/Analytics/CommunityReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_communityreport',
      reference: 'admin_pages_analytics_getcommunityreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/tier-report',
    name: 'tier-report',
    component: () => import('../views/Analytics/TierReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_tierreport',
      reference: 'admin_pages_analytics_tierreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/internal-ties-report',
    name: 'internal-ties-report',
    component: () => import('../views/Analytics/InternalTiesReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_internaltiesreport',
      reference: 'admin_pages_analytics_internaltiesreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/abuse-report',
    name: 'abuse-report',
    component: () => import('../views/Analytics/AbuseReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_abusereport',
      reference: 'admin_pages_analytics_abusereport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/access-report',
    name: 'access-report',
    component: () => import('../views/Analytics/AccessReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_accessreport',
      reference: 'admin_pages_analytics_accessreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/engagement-report',
    name: 'engagement-report',
    component: () => import('../views/Analytics/EngagementReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_engagementreport',
      reference: 'admin_pages_analytics_engagementreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/temporal-changes-report',
    name: 'temporal-changes-report',
    component: () => import('../views/Analytics/TemporalChangesReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_temporalchangesreport',
      reference: 'admin_pages_analytics_temporalchangesreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  {
    path: '/comment-report',
    name: 'comment-report',
    component: () => import('../views/Analytics/CommentReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_analytics_commentreport',
      reference: 'admin_pages_analytics_commentreport',
      store: null,
      index: PageIndex.ANALYTICS,
    },
  },
  // #endregion

  // #region 2-Company Settings
  {
    path: '/display-settings',
    name: 'display-settings',
    component: () => import('../views/Company/Display.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_displaysettings',
      reference: 'admin_components_company_display_settings',
      store: null,
      index: PageIndex.COMPANY_SETTINGS,
    },
  },
  {
    path: '/department-settings',
    name: 'department-settings',
    component: () => import('../views/Company/Department.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_departmentsettings',
      reference: 'admin_components_company_department_wrapper',
      index: PageIndex.COMPANY_SETTINGS,
      store: null,
    },
  },
  {
    path: '/position-settings',
    name: 'position-settings',
    component: () => import('../views/Company/Position.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_positionsettings',
      reference: 'admin_components_company_position_wrapper',
      index: PageIndex.COMPANY_SETTINGS,
      store: null,
    },
  },
  {
    path: '/employee-type-settings',
    name: 'employee-type-settings',
    component: () => import('../views/Company/EmployeeType.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_employeetypesettings',
      reference: 'admin_components_company_settings_employeetype_wrapper',
      index: PageIndex.COMPANY_SETTINGS,
      store: null,
    },
  },
  {
    path: '/location-settings',
    name: 'location-settings',
    component: () => import('../views/Company/Location.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_locationsettings',
      reference: 'admin_components_company_settings_location_wrapper',
      store: null,
      index: PageIndex.COMPANY_SETTINGS,
    },
  },
  {
    path: '/level-settings',
    name: 'level-settings',
    component: () => import('../views/Company/Level.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_companysettings_levelsettings',
      reference: 'admin_pages_company_settings_level_wrapper',
      store: null,
      index: PageIndex.COMPANY_SETTINGS,
    },
  },
  {
    path: '/point-settings',
    name: 'point-settings',
    component: () => import('../views/Company/PointSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_pointsettings',
      reference: 'admin_pages_point_settings',
      index: PageIndex.COMPANY_SETTINGS,
      store: 'point',
    },
  },
  {
    path: '/menu-settings',
    name: 'menu-settings',
    component: () => import('../views/Company/MenuSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_menusettings',
      reference: 'admin_components_company_menu_settings_wrapper',
      index: PageIndex.COMPANY_SETTINGS,
      store: 'menu',
    },
  },
  {
    path: '/holiday-settings',
    name: 'holiday-settings',
    component: () => import('../views/Company/Holiday.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_holidaysettings',
      reference: 'admin_company_holiday_settings',
      index: PageIndex.COMPANY_SETTINGS,
      store: 'holiday',
    },
  },
  {
    path: '/information-settings',
    name: 'information-settings',
    component: () => import('../views/Company/InformationSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_informationsettings',
      reference: 'admin_company_settings_information_settings',
      index: PageIndex.COMPANY_SETTINGS,
      store: 'popupNotification',
    },
  },
  {
    path: '/self-registration-settings',
    name: 'self-registration-settings',
    component: () => import('../views/Company/SelfRegistrationSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_selfregistrationsettings',
      reference: 'admin_company_selfregistration_settings',
      index: PageIndex.COMPANY_SETTINGS,
      store: 'selfRegistration',
    },
  },
  {
    path: '/manual-point-settings',
    name: 'manual-point-settings',
    component: () => import('../views/Company/ManualPoint.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_manualpoint',
      reference: 'admin_components_company_manualpoint_wrapper',
      index: PageIndex.COMPANY_SETTINGS,
      store: 'point',
    },
  },
  {
    path: '/integrations',
    name: 'integrations',
    component: () => import('../views/Company/Integrations.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_company_settings_integrations',
      reference: 'admin_components_company_settings_integrations_wrapper',
      index: PageIndex.COMPANY_SETTINGS,
      store: null,
    },
  },
  // #endregion

  // #region 3-Employee Management
  {
    path: '/employee-management',
    name: 'employee-management',
    component: () => import('../views/Employee/Employees.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_employee_management_employees',
      reference: 'admin_pages_employee_management_employees',
      store: null,
      index: PageIndex.EMPLOYEE_MANAGEMENT,
    },
  },
  {
    path: '/import-employee',
    name: 'import-employee',
    component: () => import('../views/Employee/ImportEmployee.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_import_employee',
      reference: 'admin_pages_employee_management_import_employee',
      store: null,
      index: PageIndex.EMPLOYEE_MANAGEMENT,
    },
  },
  {
    path: '/permissions',
    name: 'permissions',
    component: () => import('../views/Employee/Permissions.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_administrator_management_permission',
      store: null,
      reference: 'admin_pages_employee_management_permissions',
      index: PageIndex.EMPLOYEE_MANAGEMENT,
    },
  },
  {
    path: '/filters',
    name: 'filters',
    component: () => import('../views/Employee/Filters.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_administrator_management_filters',
      reference: 'admin_pages_employee_management_filters',
      store: null,
      index: PageIndex.EMPLOYEE_MANAGEMENT,
    },
  },
  {
    path: '/employee-borders',
    name: 'employee-borders',
    component: () => import('../views/Employee/EmployeeBorders.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_employee_management_profileborders',
      reference: 'admin_pages_emplooyee_management_borders',
      store: null,
      index: PageIndex.EMPLOYEE_MANAGEMENT,
    },
  },
  // #endregion

  // #region 4-Sharing Settings
  {
    path: '/company-value-settings',
    name: 'company-value-settings',
    component: () => import('../views/Post/CompanyValueSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_companyvalue',
      reference: 'admin_pages_sharing_settings_company_value',
      store: 'companyValue',
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/company-value-types',
    name: 'company-value-types',
    component: () => import('../views/Post/CompanyValueTypes.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_companyvaluetype',
      reference: 'admin_pages_sharing_settings_company_value_types',
      store: 'companyValue',
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/sharing-complaints',
    name: 'sharing-complaints',
    component: () => import('../views/Post/SharingComplaints.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_reports',
      reference: 'admin_pages_sharing_settings_sharing_complaints',
      store: null,
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/post-report-review/:id',
    name: 'post-report-reviews',
    component: () => import('../views/Post/PostReportReview.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_reportreview_reportdetail',
      reference: 'admin_pages_sharing_settings_sharing_complaints',
      store: null,
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/celebration-settings',
    name: 'celebration-settings',
    component: () => import('../views/Post/CelebrationSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_celebrationsbordersettings',
      reference: 'admin_pages_sharing_settings_celebrations_border_settings',
      store: null,
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/post-settings',
    name: 'post-settings',
    component: () => import('../views/Post/PostSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_postsettings',
      reference: 'admin_pages_sharing_settings_post_settings',
      store: 'post',
      index: 4,
    },
  },
  {
    path: '/post-cards',
    name: 'post-cards',
    component: () => import('../views/Post/PostCards.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_postcards',
      reference: 'admin_pages_sharing_settings_post_cards',
      store: 'post',
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/file-upload',
    name: 'file-upload',
    component: () => import('../views/Post/FileUpload.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_filesettings',
      reference: 'admin_pages_sharing_settings_file_upload',
      store: 'file',
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/collage-settings',
    name: 'collage-settings',
    component: () => import('../views/Post/TemplateSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_appreciation_settings_collagesettings',
      reference: 'admin_pages_sharingsettings_posttemplates',
      store: 'template',
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/post-filters',
    name: 'post-filters',
    component: () => import('../views/Post/PostFilters.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_post_settings_postfilters',
      reference: 'admin_ref_post_settings_postfilters',
      store: null,
      index: PageIndex.SHARING_SETTINGS,
    },
  },
  {
    path: '/daily-quotes',
    name: 'daily-quotes',
    component: () => import('../views/Post/DailyQuotes.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_dailyquote',
      reference: 'admin_pages_dailyquote',
      store: 'quote',
      index: 4,
    },
  },
  // #endregion

  // #region 5-Sharing Confirmations
  {
    path: '/rules',
    name: 'rules',
    component: () => import('../views/Sharing/Rules.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_sharing_rules',
      reference: 'admin_pages_sharing_confirmations_rules',
      store: 'blacklist',
      index: PageIndex.SHARING_CONFIRMATIONS,
    },
  },
  {
    path: '/post-waiting-rules',
    name: 'post-waiting-rules',
    component: () => import('../views/Sharing/PostsWaitingRules.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_sharing_deniedposts',
      reference: 'admin_pages_sharing_confirmations_objectionable_posts',
      store: 'blacklist',
      index: PageIndex.SHARING_CONFIRMATIONS,
    },
  },
  {
    path: '/objectionable-posts',
    name: 'objectionable-posts',
    component: () => import('../views/Sharing/ObjectionablePosts.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_sharing_blackposts',
      reference: 'admin_pages_sharing_confirmations_blacklist_posts',
      store: 'blacklist',
      index: PageIndex.SHARING_CONFIRMATIONS,
    },
  },
  {
    path: '/comments-waiting-rules',
    name: 'comments-waiting-rules',
    component: () => import('../views/Sharing/CommentsWaitingRules.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_sharing_deniedcomments',
      reference: 'admin_pages_sharing_confirmations_objectionable_comments',
      store: 'blacklist',
      index: PageIndex.SHARING_CONFIRMATIONS,
    },
  },
  {
    path: '/objectionable-comments',
    name: 'objectionable-comments',
    component: () => import('../views/Sharing/ObjectionableComments.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_sharing_blackcomments',
      reference: 'admin_pages_sharing_confirmations_blacklist_comments',
      store: 'blacklist',
      index: PageIndex.SHARING_CONFIRMATIONS,
    },
  },
  // #endregion

  // #region 6-Announcement Settings
  {
    path: '/announcement-settings',
    name: 'announcement-settings',
    component: () => import('../views/Announcement/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_announcement_settings_announcementlist',
      reference: 'admin_pages_announcement_management_announcement_settings',
      store: 'announcement',
      index: PageIndex.ANNOUNCEMENT_SETTINGS,
    },
  },
  {
    path: '/announcement-categories',
    name: 'announcement-categories',
    component: () => import('../views/Announcement/Category.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_announcement_settings_announcementcategorylist',
      reference: 'admin_pages_announcement_management_announcement_category_list',
      store: 'announcement',
      index: PageIndex.ANNOUNCEMENT_SETTINGS,
    },
  },
  // #endregion

  // #region 7-Performance Management
  {
    path: '/company-objectives',
    name: 'company-objectives',
    component: () => import('../views/Performance/CompanyObjectives.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_performance_management_companyobjectives',
      reference: 'admin_pages_performance_management_company_objectives',
      store: 'performance',
      index: PageIndex.PERFORMANCE_MANAGEMENT,
    },
  },
  {
    path: '/teams',
    name: 'teams',
    component: () => import('../views/Performance/Teams.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_performance_management_teams',
      reference: 'admin_pages_performance_management_teams',
      store: 'performance',
      index: PageIndex.PERFORMANCE_MANAGEMENT,
    },
  },
  {
    path: '/team-objectives/:teamId',
    name: 'team-objectives',
    component: () => import('../views/Performance/TeamObjectives.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_performance_management_teams',
      reference: 'admin_pages_performance_management_teams',
      store: 'performance',
      index: PageIndex.PERFORMANCE_MANAGEMENT,
    },
  },
  {
    path: '/performance-management-settings',
    name: 'performance-management-settings',
    component: () => import('../views/Performance/PerformanceManagementSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_performance_management_settings',
      reference: 'admin_pages_performance_management_settings',
      store: 'performance',
      index: PageIndex.PERFORMANCE_MANAGEMENT,
    },
  },
  {
    path: '/performance-management-reports',
    name: 'performance-management-reports',
    component: () => import('../views/Performance/PerformanceReports.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_performance_management_reports',
      reference: 'admin_pages_performance_management_reports',
      store: 'performance',
      index: PageIndex.PERFORMANCE_MANAGEMENT,
    },
  },

  // #endregion

  // #region 8-Custom Reward
  {
    path: '/orders',
    name: 'orders',
    component: () => import('../views/Rewards/Orders.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_reward_custom_management_orders',
      reference: 'admin_pages_reward_management_orders',
      store: 'order',
      index: PageIndex.CUSTOM_REWARD,
    },
  },
  {
    path: '/rewards',
    name: 'rewards',
    component: () => import('../views/Rewards/Rewards.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_reward_custom_management_rewards',
      reference: 'admin_pages_reward_management_rewards',
      store: 'reward',
      index: PageIndex.CUSTOM_REWARD,
    },
  },
  {
    path: '/reward-categories',
    name: 'rewards-categories',
    component: () => import('../views/Rewards/Categories.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_reward_custom_management_rewardcategories',
      reference: 'admin_pages_reward_management_reward_categories',
      store: 'reward',
      index: PageIndex.CUSTOM_REWARD,
    },
  },
  // #endregion

  // #region 9-Survey
  {
    path: '/regular-surveys',
    name: 'regular-surveys',
    component: () => import('../views/Survey/RegularSurveys.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_survey_surveyreport_surveysettings_regularsurveys',
      reference: 'admin_pages_survey_settings_regular_surveys',

      store: 'survey',
      index: PageIndex.SURVEY,
    },
  },
  {
    path: '/repeatable-surveys',
    name: 'repeatable-surveys',
    component: () => import('../views/Survey/RepeatableSurveys.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_survey_surveyreport_surveysettings_repeatablesurveys',
      reference: 'admin_pages_survey_settings_repeatable_surveys',
      store: 'survey',
      index: PageIndex.SURVEY,
    },
  },
  {
    path: '/survey-details/:id',
    name: 'survey-details',
    component: () => import('../views/Survey/SurveyDetails.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_sidebar_surveysettings',
      reference: null,
      store: 'survey',
      index: PageIndex.SURVEY,
    },
  },
  {
    path: '/survey-report/:surveyId',
    name: 'survey-report',
    component: () => import('../views/Survey/SurveyReports.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_survey_report',
      reference: null,
      store: null,
      index: PageIndex.SURVEY,
    },
  },
  {
    path: '/view-surveys/:parentSurveyId',
    name: 'view-surveys',
    component: () => import('../views/Survey/ViewRepeatableSurveys.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_survey_surveyreport_surveysettings_repeatablesurveys',
      reference: null,
      store: null,
      index: PageIndex.SURVEY,
    },
  },
  // #endregion

  // #region 10-Answerwin
  {
    path: '/answerwin-settings',
    name: 'answerwin-settings',
    component: () => import('../views/AnswerWinSettings/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_answerwin',
      reference: 'admin_pages_answer_win_settings',
      store: 'answerWin',
      index: PageIndex.ANSWERWIN,
    },
  },
  {
    path: '/answerwin-questions/:answerWinId',
    name: 'answerwin-questions',
    component: () => import('../views/AnswerWinSettings/AnswerWinQuestion.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_answerwinquestions',
      reference: 'admin_pages_answer_win_settings',
      store: 'answerWin',
      index: PageIndex.ANSWERWIN,
    },
  },
  {
    path: '/answerwin-reports/:answerWinId',
    name: 'answerwin-reports',
    component: () => import('../views/AnswerWinSettings/AnswerwinReports.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_answerwin_report',
      reference: 'admin_pages_answer_win_settings',
      store: 'answerWin',
      index: PageIndex.ANSWERWIN,
    },
  },
  // #endregion

  // #region 11-Challenge Settings
  {
    path: '/challenge-settings',
    name: 'challenge-settings',
    component: () => import('../views/Challenge/ChallengeSettings.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_challenge_settings',
      reference: 'admin_pages_challenge_settings',
      store: 'challenge',
      index: PageIndex.CHALLENGE_SETTINGS,
    },
  },
  {
    path: '/challenge-tasks/:challengeId',
    name: 'challenge-tasks',
    component: () => import('../views/Challenge/ChallengeTasks.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_challenge_tasks',
      reference: 'admin_pages_challenge_settings',
      store: 'challenge',
      index: PageIndex.CHALLENGE_SETTINGS,
    },
  },
  {
    path: '/challenge-reports/:challengeId',
    name: 'challenge-reports',
    component: () => import('../views/Challenge/ChallengeReports.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_global_reports',
      reference: 'admin_pages_challenge_settings',
      store: 'challenge',
      index: 11,
    },
  },
  // #endregion

  // #region 12-Word Game
  {
    path: '/word-game-settings',
    name: 'word-game-settings',
    component: () => import('../views/WordGame/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_word_game',
      reference: 'admin_pages_wordgame',
      store: 'wordGame',
      index: PageIndex.WORD_GAME,
    },
  },
  {
    path: '/word-game-report/:wordGameId',
    name: 'word-game-report',
    component: () => import('../views/WordGame/WordGameReport.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_word_game_report',
      reference: 'admin_pages_wordgame',
      store: 'wordGame',
      index: PageIndex.WORD_GAME,
    },
  },
  // #endregion

  // #region 13-Notification Settings
  {
    path: '/notification-management',
    name: 'notification-management',
    component: () => import('../views/NotificationSettings/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_notification',
      reference: 'admin_pages_notification_management',
      store: 'notification',
      index: PageIndex.NOTIFICATION_SETTINGS,
    },
  },
  {
    path: '/notification-edit/:id',
    name: 'notification-edit',
    component: () => import('../views/NotificationSettings/NotificationEdit.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_notificationedit',
      reference: 'admin_pages_notification_management',
      store: 'notification',
      index: PageIndex.NOTIFICATION_SETTINGS,
    },
  },
  // #endregion

  // #region 14-Community
  {
    path: '/community',
    name: 'community',
    component: () => import('../views/Community/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_community',
      reference: 'admin_pages_community_settings',
      store: 'community',
      index: PageIndex.COMMUNITY,
    },
  },
  // #endregion

  // #region 15-GDPR Settings
  {
    path: '/gdpr-settings',
    name: 'gdpr-settings',
    component: () => import('../views/Hekate/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_hekate_gdpr',
      reference: 'admin_pages_gdpr_settings',
      store: 'hekate',
      index: PageIndex.GDPR,
    },
  },
  // #endregion

  // #region 16-Security Settings
  {
    path: '/multifactor-authentication',
    name: 'multifactor-authentication',
    component: () => import('../views/Security/MultiFactorAuthentication.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_security_multifactorauthentication',
      reference: 'admin_pages_security_settings_multi_factor_authentication',
      store: null,
      index: PageIndex.SECURITY,
    },
  },
  {
    path: '/secure-password',
    name: 'secure-password',
    component: () => import('../views/Security/SecurePassword.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_security_securepassword',
      reference: 'admin_pages_security_settings_secure_password',
      store: null,
      index: PageIndex.SECURITY,
    },
  },
  // #endregion

  // #region 17-System Pages
  {
    path: '/companies',
    name: 'companies',
    component: () => import('../views/Companies/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_companies',
      reference: 'admin_system_api_companies_get_list',
      store: null,
      index: PageIndex.SYSTEM,
    },
  },
  {
    path: '/notification-template-settings',
    name: 'notification-template-settings',
    component: () => import('../views/NotificationTemplates/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_sidebar_notificationtemplates',
      reference: 'admin_system_apigetnotificationtemplate',
      store: 'notification',
      index: PageIndex.SYSTEM,
    },
  },
  {
    path: '/message-templates',
    name: 'message-templates',
    component: () => import('../views/MessageTemplates/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_sidebar_messagetemplates',
      reference: 'admin_system_api_getmessagetemplate',
      store: 'messageTemplate',
      index: PageIndex.MESSAGE_TEMPLATES,
    },
  },
  {
    path: '/language-settings',
    name: 'language-settings',
    component: () => import('../views/Language/index.vue'),
    meta: {
      isDefault: true,
      pageResource: 'admin_components_languagesettings_languagesettings',
      reference: 'admin_system_api_getresourceedit',
      store: null,
      index: PageIndex.LANGUAGE_SETTINGS,
    },
  },
  // #endregion

  // #region Development, Not Found, No Permission
  {
    path: '/development',
    name: 'development',
    component: () => {
      if (import.meta.env.DEV) {
        return import('../views/Development/index.vue')
      } else {
        return import('../views/User/NotFound.vue')
      }
    },
    meta: {
      isDefault: true,
      pageResource: 'Development',
      reference: null,
      store: null,
    },
  },
  {
    path: '/no-permission',
    name: 'no-permission',
    component: () => {
      return import('../views/User/NoPermission.vue')
    },
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_no_permission',
      reference: null,
      store: null,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => {
      return import('../views/User/NotFound.vue')
    },
    meta: {
      isDefault: true,
      pageResource: 'admin_pages_not_found',
      reference: null,
      store: null,
    },
  },

  // #endregion
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.afterEach((to, from) => {
  const store = from.meta.store as string | null
  if (store && to.path !== from.path) {
    resetStore(store)
  }
})

router.beforeEach(async (to, from) => {
  const refreshOutcome = to.meta.isDefault ? await refreshAccessTokenForRoute() : null

  // A temporary refresh failure must not clear the session or force /login.
  // Keep the current route; API calls can surface the transient error normally.
  if (
    refreshOutcome?.status === 'transient' &&
    (!GetToken() || TokenManager.GetInstance().IsTokenExpired())
  ) {
    return true
  }

  if (shouldRedirect(to)) {
    TokenManager.GetInstance().ClearTokens()
    return '/login'
  }

  if (shouldCheckPermissions(to)) {
    const hasPermission = await checkPermissions(to, from)
    if (!hasPermission) {
      return '/no-permission'
    }
  }

  return true
})

function shouldRedirect(to: any) {
  return to.meta.isDefault && GetToken() === null && GetRefreshToken() === null && !HasSessionHint()
}

function shouldCheckPermissions(to: any) {
  return to.meta.reference !== null
}

async function checkPermissions(to: any, from: any) {
  const permissionList = await useDataStore().GetFlattenList(from.name === undefined)
  return permissionList.includes(to.meta.reference)
}

export default router
